import {Badge} from '@mantine/core';
import {Box} from 'src/design/atoms/Box';
import {Group} from 'src/design/atoms/Group';
import {Text} from 'src/design/atoms/Text';
import React, {useState} from 'react';
import type {
  CourseDefinitionContentResponse,
  CourseSessionContentResponse,
  CourseSessionCurrentContentResponse,
  CourseSessionQuizQuestionResponse,
} from 'src/api/ProactApiClient';
import {
  CourseDefinitionContentTypeEnum,
  CourseSessionContentStateEnum,
} from 'src/api/ProactApiClient';
import {TrackerActiveSessionDemonstration} from 'src/pages/Modules/Training/shared/courseSession/TrackerActiveSessionDemonstration';
import {TrackerActiveSessionLesson} from 'src/pages/Modules/Training/shared/courseSession/TrackerActiveSessionLesson';
import {TrackerActiveSessionQuiz} from 'src/pages/Modules/Training/shared/courseSession/TrackerActiveSessionQuiz';

export interface LivePreviewPaneProps {
  content?: CourseDefinitionContentResponse;
  index: number;
  totalContent: number;
}

/**
 * The session components resolve a session row from its definition by id, and
 * a question the author has only just added has no server id yet — every
 * unsaved question would resolve to `undefined` and crash `SessionQuestion`.
 * The preview therefore runs against a clone whose missing ids are filled with
 * synthetic negative ones (real ids are always positive), so draft and saved
 * questions match by the same mechanism.
 */
const withPreviewIds = (
  content: CourseDefinitionContentResponse
): CourseDefinitionContentResponse => {
  if (content.type !== CourseDefinitionContentTypeEnum.Quiz || !content.quiz)
    return content;

  return {
    ...content,
    quiz: {
      ...content.quiz,
      questions: (content.quiz.questions ?? []).map(
        (question, questionIndex) => ({
          ...question,
          id: question.id ?? -(questionIndex + 1),
          answers: (question.answers ?? []).map((answer, answerIndex) => ({
            ...answer,
            id: answer.id ?? -(questionIndex * 1000 + answerIndex + 1),
          })),
        })
      ),
    },
  };
};

/**
 * Identity of the quiz's question/answer structure. The skeleton is reseeded
 * whenever this changes — adding, deleting or reordering questions or answers
 * invalidates the rows the session components look up, while editing a
 * question's text does not.
 */
const quizStructureKey = (content?: CourseDefinitionContentResponse): string =>
  content?.type === CourseDefinitionContentTypeEnum.Quiz
    ? (content.quiz?.questions ?? [])
        .map(q => `${q.id ?? ''}:${(q.answers ?? []).map(a => a.id ?? '').join(',')}`)
        .join('|')
    : '';

/**
 * Builds the same shape of per-question `CourseSessionQuizQuestion` skeleton
 * the backend seeds for a real session (see
 * `CourseSessionProgressionService` — one row per definition question, one
 * answer row per definition answer, everything unselected) so the real
 * `TrackerActiveSessionQuiz` / `SessionQuestion` components — which assume
 * that skeleton always exists — don't crash when mounted against a draft
 * definition instead of a live session.
 */
const buildQuizSessionSkeleton = (
  content: CourseDefinitionContentResponse
): CourseSessionContentResponse => {
  const quiz = content.quiz;
  const questions: CourseSessionQuizQuestionResponse[] = (
    quiz?.questions ?? []
  ).map(question => ({
    isAnswersCorrect: false,
    questionDefinitionId: question.id,
    answers: (question.answers ?? []).map(answer => ({
      isSelected: false,
      answerDefinitionId: answer.id,
    })),
  }));

  return {
    state: CourseSessionContentStateEnum.NotStarted,
    contentDefinitionId: content.id ?? 0,
    sessionId: 0,
    quizId: quiz?.id,
    quiz: {
      isPassed: false,
      earnedScore: 0,
      quizDefinitionId: quiz?.id,
      questions,
    },
  };
};

/**
 * Column 3 of the Build canvas: the REAL trainee-facing session components
 * (`courseSession/Tracker*`), mounted read-only against the draft
 * definition instead of a live session — no backend session is created,
 * quiz answers are held in local-only state and never submitted. This
 * replaces the parallel `editCourseDefinition/preview/*` component tree,
 * which re-implemented (and could drift from) what trainees actually see.
 *
 * Quiz content is seeded with an in-memory session skeleton (see
 * `buildQuizSessionSkeleton`) any time the selected content switches, so the
 * real quiz components always find the per-question row they expect.
 */
export const LivePreviewPane: React.FC<LivePreviewPaneProps> = ({
  content,
  index,
  totalContent,
}) => {
  const normalizedContent = content ? withPreviewIds(content) : undefined;
  const seedKey = `${normalizedContent?.invariantId ?? ''}::${quizStructureKey(
    normalizedContent
  )}`;

  const [seededForKey, setSeededForKey] = useState(seedKey);
  const [sessionContent, setSessionContent] = useState<
    CourseSessionContentResponse | undefined
  >(() =>
    normalizedContent?.type === CourseDefinitionContentTypeEnum.Quiz
      ? buildQuizSessionSkeleton(normalizedContent)
      : undefined
  );

  // Reset (adjusted synchronously during render, so the seeded skeleton is
  // in place before the real session components below ever mount/re-render
  // against it — an effect would run one tick too late) whenever the quiz's
  // question/answer structure changes, including when a question or answer
  // is added while the same content item stays selected (a bare invariantId
  // check would miss that and leave the newly added, id-less row out of the
  // skeleton).
  if (seedKey !== seededForKey) {
    setSeededForKey(seedKey);
    setSessionContent(
      normalizedContent?.type === CourseDefinitionContentTypeEnum.Quiz
        ? buildQuizSessionSkeleton(normalizedContent)
        : undefined
    );
  }

  if (!normalizedContent) {
    return (
      <Text size="sm" c="dimmed">
        Select a content item to preview it here.
      </Text>
    );
  }

  const handleSessionContentChange = (
    updated: CourseSessionContentResponse
  ) => {
    setSessionContent(updated);
  };

  const contentResponse: CourseSessionCurrentContentResponse = {
    contentDefinition: normalizedContent,
    contentSession: sessionContent,
    currentContextIndex: index,
    totalContentLength: totalContent,
    hasContentRemaining: index < totalContent - 1,
  };

  return (
    <Box>
      <Group justify="space-between" mb="sm">
        <Badge color="blue" variant="light">
          Preview
        </Badge>
      </Group>

      {normalizedContent.type === CourseDefinitionContentTypeEnum.Lesson && (
        <TrackerActiveSessionLesson
          contentResponse={contentResponse}
          onVideoEnd={() => {}}
          onVideoTimeEventUpdate={() => {}}
        />
      )}
      {normalizedContent.type === CourseDefinitionContentTypeEnum.Quiz && (
        <TrackerActiveSessionQuiz
          contentResponse={contentResponse}
          onSessionContentChange={handleSessionContentChange}
          onSubmit={() => {}}
        />
      )}
      {normalizedContent.type === CourseDefinitionContentTypeEnum.Demonstration && (
        <TrackerActiveSessionDemonstration contentResponse={contentResponse} />
      )}
    </Box>
  );
};
